import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Globe, Search, User, CheckCircle, Clock, ArrowRight, DollarSign, AlertCircle, X, Landmark, Lock, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const SendMoney = () => {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1); // 1: search, 2: amount, 3: confirm, 4: success
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [miniBankBalance, setMiniBankBalance] = useState({
    total: 0,
    protected: 0,
    available: 0
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchRecentTransfers();
    fetchMiniBankBalance();
  }, []);

  const fetchMiniBankBalance = async () => {
    try {
      const token = localStorage.getItem('susu_token');
      
      // Get dashboard stats
      const statsResponse = await fetch(`${API_URL}/api/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      // Get protection deposits
      const protectionResponse = await fetch(`${API_URL}/api/protection-deposits`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      let totalSavings = 0;
      let totalProtected = 0;
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        totalSavings = statsData.total_savings || 0;
        totalProtected = statsData.total_protection_deposits || 0;
      }
      
      // Available = Total - Protected (minimum 0)
      const available = Math.max(0, totalSavings - totalProtected);
      
      setMiniBankBalance({
        total: totalSavings,
        protected: totalProtected,
        available: available
      });
      
    } catch (error) {
      console.error('Error fetching Mini Bank balance:', error);
    }
  };

  const fetchRecentTransfers = async () => {
    try {
      const token = localStorage.getItem('susu_token');
      const response = await fetch(`${API_URL}/api/transfers/recent`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRecentTransfers(data.transfers || []);
      }
    } catch (error) {
      console.error('Error fetching transfers:', error);
    }
  };

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const token = localStorage.getItem('susu_token');
      const response = await fetch(`${API_URL}/api/users/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.users || []);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Debounced search
    const timeoutId = setTimeout(() => searchUsers(value), 300);
    return () => clearTimeout(timeoutId);
  };

  const selectRecipient = (user) => {
    setSelectedRecipient(user);
    setStep(2);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleAmountSubmit = () => {
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (amountNum > miniBankBalance.available) {
      toast.error(`Insufficient available balance. You have $${miniBankBalance.available.toFixed(2)} available (${miniBankBalance.protected > 0 ? `$${miniBankBalance.protected.toFixed(2)} is protected` : ''})`);
      return;
    }
    setStep(3);
  };

  const handleSendMoney = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('susu_token');
      const response = await fetch(`${API_URL}/api/transfers/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          recipient_id: selectedRecipient.user_id,
          amount: parseFloat(amount),
          note: note
        })
      });

      if (response.ok) {
        setStep(4);
        fetchRecentTransfers();
        fetchMiniBankBalance();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Transfer failed');
      }
    } catch (error) {
      toast.error('Transfer failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setStep(1);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedRecipient(null);
    setAmount('');
    setNote('');
  };

  const quickAmounts = [10, 25, 50, 100];

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Landmark className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">🏦 Mini Bank</h2>
              <p className="text-amber-100 text-sm">Your savings, your way</p>
            </div>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            className="bg-white text-amber-600 hover:bg-amber-50"
            data-testid="send-money-btn"
          >
            Send Money
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      <div className="p-6">
        {/* Mini Bank Balance Breakdown */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-amber-800">Mini Bank Balance</span>
          </div>
          
          {/* Total Balance */}
          <div className="text-3xl font-bold text-amber-700 mb-3">
            ${miniBankBalance.total.toFixed(2)}
          </div>
          
          {/* Balance Breakdown */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/60 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 mb-1">
                <DollarSign className="w-3.5 h-3.5" />
                Available to Send
              </div>
              <div className="text-lg font-bold text-emerald-700">
                ${miniBankBalance.available.toFixed(2)}
              </div>
            </div>
            <div className="bg-white/60 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-xs text-amber-600 mb-1">
                <Lock className="w-3.5 h-3.5" />
                Protected
              </div>
              <div className="text-lg font-bold text-amber-700">
                ${miniBankBalance.protected.toFixed(2)}
              </div>
            </div>
          </div>
          
          {miniBankBalance.protected > 0 && (
            <p className="text-xs text-amber-600 mt-3">
              🛡️ Protected funds secure your club memberships
            </p>
          )}
        </div>

        {/* Recent Transfers */}
        <div className="mb-4">
          <h3 className="font-semibold text-stone-900 mb-3">Recent Transfers</h3>
          {recentTransfers.length === 0 ? (
            <div className="text-center py-8 bg-stone-50 rounded-xl">
              <Clock className="w-10 h-10 text-stone-300 mx-auto mb-2" />
              <p className="text-stone-500 text-sm">No recent transfers</p>
              <p className="text-stone-400 text-xs mt-1">Send money to get started!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTransfers.slice(0, 3).map((transfer, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-medium text-stone-900">{transfer.recipient_name}</div>
                      <div className="text-xs text-stone-500">{transfer.date}</div>
                    </div>
                  </div>
                  <div className={`font-bold ${transfer.type === 'sent' ? 'text-red-500' : 'text-green-500'}`}>
                    {transfer.type === 'sent' ? '-' : '+'}${transfer.amount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* International Remittance Coming Soon */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-bold text-amber-800">International Remittance</h4>
              <p className="text-sm text-amber-700 mt-1">
                Coming Soon! Send money directly to bank accounts and mobile wallets worldwide with competitive exchange rates.
              </p>
              <div className="flex gap-2 mt-3">
                <span className="px-2 py-1 bg-amber-100 rounded-full text-xs text-amber-700">Wise</span>
                <span className="px-2 py-1 bg-amber-100 rounded-full text-xs text-amber-700">Remitly</span>
                <span className="px-2 py-1 bg-amber-100 rounded-full text-xs text-amber-700">Bank Transfer</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Money Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && resetModal()}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-stone-200 bg-gradient-to-r from-amber-50 to-orange-50">
                <div className="flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-amber-600" />
                  <h3 className="text-lg font-bold text-stone-900">
                    {step === 1 && 'Find Recipient'}
                    {step === 2 && 'Enter Amount'}
                    {step === 3 && 'Confirm Transfer'}
                    {step === 4 && 'Transfer Complete!'}
                  </h3>
                </div>
                <button
                  onClick={resetModal}
                  className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-stone-500" />
                </button>
              </div>

              <div className="p-6">
                {/* Step 1: Search Recipient */}
                {step === 1 && (
                  <div>
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={handleSearch}
                        className="pl-10"
                        data-testid="recipient-search-input"
                      />
                    </div>

                    {searchLoading && (
                      <div className="text-center py-4">
                        <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                      </div>
                    )}

                    {searchResults.length > 0 && (
                      <div className="space-y-2">
                        {searchResults.map((user) => (
                          <div
                            key={user.user_id}
                            onClick={() => selectRecipient(user)}
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-emerald-50 cursor-pointer transition-colors border border-stone-200"
                            data-testid={`recipient-${user.user_id}`}
                          >
                            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                              {user.picture ? (
                                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                              ) : (
                                <User className="w-5 h-5 text-emerald-600" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-stone-900">{user.name}</div>
                              <div className="text-xs text-stone-500">{user.email}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
                      <div className="text-center py-8">
                        <User className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                        <p className="text-stone-500 text-sm">No users found</p>
                      </div>
                    )}

                    {searchQuery.length < 2 && (
                      <div className="text-center py-8 text-stone-500 text-sm">
                        Enter at least 2 characters to search
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Enter Amount */}
                {step === 2 && selectedRecipient && (
                  <div>
                    <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl mb-6">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-stone-900">{selectedRecipient.name}</div>
                        <div className="text-sm text-stone-500">{selectedRecipient.email}</div>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-stone-700 mb-2">Amount (USD)</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                        <Input
                          type="number"
                          placeholder="0.00"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="pl-10 text-2xl font-bold"
                          data-testid="send-amount-input"
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs mt-2">
                        <span className="text-emerald-600 font-medium">
                          Available: ${miniBankBalance.available.toFixed(2)}
                        </span>
                        {miniBankBalance.protected > 0 && (
                          <span className="text-amber-600 flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            ${miniBankBalance.protected.toFixed(2)} protected
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 mb-6">
                      {quickAmounts.map((amt) => (
                        <Button
                          key={amt}
                          variant="outline"
                          size="sm"
                          onClick={() => setAmount(amt.toString())}
                          className="flex-1"
                          disabled={amt > miniBankBalance.available}
                        >
                          ${amt}
                        </Button>
                      ))}
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-stone-700 mb-2">Note (optional)</label>
                      <Input
                        placeholder="Add a note..."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        data-testid="send-note-input"
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                        Back
                      </Button>
                      <Button onClick={handleAmountSubmit} className="flex-1 bg-amber-500 hover:bg-amber-600">
                        Continue
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Confirm */}
                {step === 3 && selectedRecipient && (
                  <div>
                    <div className="bg-stone-50 rounded-xl p-4 mb-6">
                      <div className="flex justify-between mb-3">
                        <span className="text-stone-600">To</span>
                        <span className="font-medium text-stone-900">{selectedRecipient.name}</span>
                      </div>
                      <div className="flex justify-between mb-3">
                        <span className="text-stone-600">Amount</span>
                        <span className="font-bold text-2xl text-amber-600">${parseFloat(amount).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between mb-3">
                        <span className="text-stone-600">From</span>
                        <span className="font-medium text-stone-900">🏦 Mini Bank</span>
                      </div>
                      {note && (
                        <div className="flex justify-between">
                          <span className="text-stone-600">Note</span>
                          <span className="text-stone-900">{note}</span>
                        </div>
                      )}
                    </div>

                    <div className="bg-amber-50 rounded-xl p-3 mb-6 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">
                        This transfer is instant and cannot be reversed. Please verify the recipient.
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                        Back
                      </Button>
                      <Button 
                        onClick={handleSendMoney} 
                        className="flex-1 bg-amber-500 hover:bg-amber-600"
                        disabled={loading}
                        data-testid="confirm-send-btn"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <>Send ${parseFloat(amount).toFixed(2)}</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 4: Success */}
                {step === 4 && (
                  <div className="text-center py-6">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                      className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"
                    >
                      <CheckCircle className="w-10 h-10 text-amber-600" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-stone-900 mb-2">Transfer Successful! 🏦</h3>
                    <p className="text-stone-600 mb-6">
                      ${parseFloat(amount).toFixed(2)} sent from your Mini Bank to {selectedRecipient?.name}
                    </p>
                    <Button onClick={resetModal} className="bg-amber-500 hover:bg-amber-600">
                      Done
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SendMoney;
